import {
  Button,
  Heading,
  HStack,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { isInitialAppointment } from '@web-app/utils/appointment/appointment-type.utils';

interface TimeLimitNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment?: AppointmentResponse;
}

const initialListItems = [
  { emoji: '✅', text: 'Schedule the next 3 (or more) visits' },
  {
    emoji: '✅',
    text: 'Suggest weekly sessions to build momentum and create accountability',
  },
  { emoji: '✅', text: 'Confirm coverage and a plan for future sessions' },
  {
    emoji: '📈',
    text: (
      <Text as="span">
        Patients with recurring appointments are{' '}
        <Text as="b">more likely to reach their goals!</Text>
      </Text>
    ),
  },
];

const followupListItems = [
  { emoji: '✅', text: 'Book the next session' },
  { emoji: '✅', text: 'Consider setting a recurring time' },
  { emoji: '✅', text: 'Recap and celebrate progress!' },
  {
    emoji: '📈',
    text: 'Small wins add up - help your patient stay excited and consistent!',
  },
];

const TimeLimitNotificationModal: React.FC<TimeLimitNotificationModalProps> = ({
  isOpen,
  onClose,
  appointment,
}) => {
  const handleOpenHealthie = () => {
    window.open(
      `${process.env.NEXT_PUBLIC_HEALTHIE_URL}/providers/${appointment?.provider_id}/appointments`,
      '_blank',
    );
  };

  const isInitial = isInitialAppointment(
    appointment?.healthie?.appointment_type?.name,
  );
  const listItems = isInitial ? initialListItems : followupListItems;

  const isFollowUp = appointment?.healthie?.appointment_type?.name
    ?.toLowerCase()
    .includes('follow');
  if (!isInitial && !isFollowUp) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        position="absolute"
        top="20px"
        bg="surface.default"
        overflow="hidden"
        borderColor="border.default"
        borderWidth={1}
        borderRadius={16}
        color="text.default"
        width="400px"
      >
        <ModalBody p={4}>
          <VStack spacing={6} width="100%" alignItems="flex-start">
            <VStack spacing={0} width="100%" alignItems="flex-start">
              <Heading
                size="sm"
                fontFamily="Plus Jakarta Sans"
                fontWeight="bold"
                mb={4}
              >
                {isInitial
                  ? 'Before you wrap up your first session:'
                  : 'Before you wrap up your session:'}
              </Heading>
              <List spacing={3} width="100%">
                {' '}
                {/* Adjusted spacing for ListItems */}
                {listItems.map((item, index) => (
                  <ListItem key={index}>
                    <HStack alignItems="flex-start" spacing={2}>
                      {' '}
                      {/* Use HStack for layout */}
                      <Text pt={0.5}>{item.emoji}</Text> {/* Emoji */}
                      <Text color="text.default">{item.text}</Text>{' '}
                      {/* Text content */}
                    </HStack>
                  </ListItem>
                ))}
              </List>
            </VStack>
            <HStack width="100%" spacing={4}>
              <Button
                flex={1}
                variant="outline"
                onClick={onClose}
                borderRadius="16px"
                color="text.default"
                borderColor="border.subtle"
                _hover={{ bg: 'surface.subtle' }}
              >
                Dismiss
              </Button>
              <Button
                flex={1}
                colorScheme="orange"
                color="white"
                onClick={handleOpenHealthie}
                borderRadius="16px"
                _hover={{ bg: 'orange.600' }}
              >
                Open Schedule
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TimeLimitNotificationModal;
